// FOV (field of view): the base slider setting, Minecraft's sprint-widening
// effect, and a smooth per-frame settle — kept pure so the clamp / caption /
// effect / smoothing are unit-tested directly. The projection matrix itself is
// applied in boot's render loop, which reads settings.fov + the live sprint
// state each frame; the Options slider only mutates settings.fov.

export const FOV_MIN = 30;
export const FOV_MAX = 110;
export const FOV_DEFAULT = 70; // Minecraft's "Normal"

/** How much sprinting widens the view (Minecraft's speed-based FOV effect). */
export const SPRINT_FOV_MUL = 1.1;

/** Fraction of the remaining gap the rendered FOV closes each frame. */
export const FOV_APPROACH = 0.5;

/** Below this the rendered FOV snaps to its target, so it settles exactly
 *  (no perpetual sub-degree updates driving needless projection rebuilds). */
const FOV_EPSILON = 0.05;

/** Clamp a raw setting to the integer [30, 110] slider range. */
export function clampFov(v: number): number {
  if (!Number.isFinite(v)) return FOV_DEFAULT;
  return Math.max(FOV_MIN, Math.min(FOV_MAX, Math.round(v)));
}

/** The slider caption — Minecraft names the two anchor stops, else the number. */
export function fovLabel(v: number): string {
  const f = clampFov(v);
  if (f === FOV_DEFAULT) return 'Normal';
  if (f === FOV_MAX) return 'Quake Pro';
  return String(f);
}

/** The FOV the camera should settle at, given the base setting and whether the
 *  player is actively sprinting (the widening only applies while sprinting). */
export function effectiveFov(base: number, sprinting: boolean): number {
  const f = clampFov(base);
  return sprinting ? f * SPRINT_FOV_MUL : f;
}

/** One smoothing step from the current rendered FOV toward the target; snaps
 *  when within FOV_EPSILON so it settles exactly on the target. */
export function approachFov(current: number, target: number): number {
  const d = target - current;
  if (Math.abs(d) <= FOV_EPSILON) return target;
  return current + d * FOV_APPROACH;
}
